import React from 'react'
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react'
import styles from './MetricCard.module.scss'

interface Breakdown {
  [key: string]: number
}

interface MetricCardProps {
  title: string
  value: number
  delta?: number
  breakdown?: Breakdown
  type?: 'total' | 'success' | 'error' | 'warning' | 'info' | 'neutral'
  loading?: boolean
  total?: number // For calculating percentage
  invertDelta?: boolean // When true, increase is bad (red), decrease is good (green)
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  delta,
  breakdown,
  type = 'neutral',
  loading,
  total,
  invertDelta = false,
}) => {
  if (loading) return <div className={`${styles.card} ${styles.loading}`}></div>

  const isPositive = delta !== undefined && delta >= 0
  // When invertDelta is true, positive changes are bad (red), negative changes are good (green)
  const isGoodChange = invertDelta ? !isPositive : isPositive
  const deltaClass = delta === undefined ? '' : isGoodChange ? styles.deltaPositive : styles.deltaNegative

  // Calculate percentage only if total is provided and value isn't the total itself
  const percentage = total && total > 0 && type !== 'total' ? ((value / total) * 100).toFixed(1) : null

  return (
    <div className={`${styles.card} ${styles[type]}`}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        {delta !== undefined && delta !== 0 && (
          <div className={`${styles.delta} ${deltaClass}`}>
            {isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            <span>{Math.abs(delta).toLocaleString()}</span>
          </div>
        )}
        {delta === 0 && (
          <span className={styles.noChange}>
            <Minus size={14} />
          </span>
        )}
      </div>

      <div className={styles.valueRow}>
        <div className={styles.mainValue}>{value?.toLocaleString() || 0}</div>
        {percentage && <span className={styles.percentage}>{percentage}%</span>}
      </div>

      {breakdown && Object.keys(breakdown).length > 0 && (
        <div className={styles.breakdown}>
          {Object.entries(breakdown)
            .slice(0, 4)
            .map(([key, count]) => (
              <div key={key} className={styles.breakdownItem}>
                <span className={styles.bdLabel}>{key}</span>
                <span className={styles.bdValue}>{count.toLocaleString()}</span>
              </div>
            ))}
          {Object.keys(breakdown).length > 4 && (
            <div className={styles.breakdownMore}>+ {Object.keys(breakdown).length - 4} more</div>
          )}
        </div>
      )}
    </div>
  )
}

export default MetricCard
